import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { PermissionGrid } from "@/components/admin/PermissionGrid";

export function UserPermissionsDialog({ user, meta, onClose, onSaved }) {
  const [ov, setOv] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (user) setOv(user.overrides || {}); }, [user]);
  const base = (meta?.roles || []).find((r) => r.name === user?.role)?.permissions || [];
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/users/${user.id}/permissions`, { overrides: ov }); toast.success("Permissions updated — effective immediately"); onSaved(); onClose(); }
    catch (e) { if (e?.message !== "cancelled") toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-card" data-testid="user-perms-dialog">
        <DialogHeader><DialogTitle className="font-head">Manage Permissions · {user?.name} <span className="text-sm font-normal text-muted-foreground">({user?.role})</span></DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Inherit = use the {user?.role} role default. Allow / Deny override it for this user only. Deny always wins. Dot = effective access.</p>
        {user?.role === "Super Admin" ? <p className="text-sm">Super Admin permissions cannot be modified.</p>
          : <PermissionGrid catalog={meta?.catalog || []} mode="user" value={ov} base={base} onChange={setOv} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || user?.role === "Super Admin"} data-testid="user-perms-save">{saving ? "Saving…" : "Save Permissions"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState(""); const [cf, setCf] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); setCf(""); }, [user]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/users/${user.id}/reset-password`, { password: pw, confirm_password: cf }); toast.success(`Password reset for ${user.name}. All their sessions were signed out.`); onClose(); }
    catch (e) { if (e?.message !== "cancelled") toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="font-head">Reset Password · {user?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>New Password</Label><Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="rp-password" /></div>
          <div><Label>Confirm Password</Label><Input type="password" autoComplete="new-password" value={cf} onChange={(e) => setCf(e.target.value)} data-testid="rp-confirm" /></div>
          <p className="text-[11px] text-muted-foreground">Min 8 characters with letters and numbers. You'll be asked to re-enter your own password.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy || !pw} data-testid="rp-save">{busy ? "Saving…" : "Reset Password"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
